import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import { NotificationManager } from 'react-notifications';
import Button from '../../../common/Button';
import { CurrencyValue } from '../../../common/currencyValue';
import { constants } from '../../../common/constants/constants';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import CacheData from '../../../common/utils/cacheData';
import { showLoader, hideLoader } from '../../../store/common/slice';
import { fetchBuildingData } from "../../../store/buildings/actions";
import manager from '../../../images/newDesign/manager.svg';
import disabledManager from '../../../images/newDesign/manager_disabled.svg';
import hammer from '../../../images/newDesign/hammer.svg';
import disabledHammer from '../../../images/newDesign/hammer_disabled.svg';
import star from '../../../images/newDesign/star.svg';
import disabledStar from '../../../images/newDesign/star_disabled.svg';
import arrowLeft from '../../../images/arrow-left.png';
import { StyledPopover } from '../debt_list/debtList.styled';
import { ApartmentInfoStyled } from './TenantList.styled';
import Dots from './Dots';
import AddOrEditContact from './AddOrEditContact';
import { isAdminSelector } from '../../../store/auth/selectors';
import { parsePhoneToInternational } from '../../../common/utils/parseFunctions';

const ApartmentInfo = ({ apt, buildingID, loadApartments, edit, setEdit, add, setAdd, apartments, setApartments }) => {
    const { t } = useTranslation();
    const client = useAccessToken();
    const dispatch = useDispatch();
    const history = useHistory();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const currentContact = CacheData.fetchCache('currentContactInTenantList');
    const isAdmin = useSelector(isAdminSelector);

    const dropdownTypeOptions = [
        {
            value: constants.APIContract.tenantTypes.renter,
            label: t('payments.renter')
        },
        {
            value: constants.APIContract.tenantTypes.owner,
            label: t('payments.owner')
        }
    ];

    const [tenants, setTenants] = useState([]);
    const [contactsNum, setContactsNum] = useState();
    const [showPopover, setShowPopover] = useState(false);
    const [selected, setSelected] = useState(1);
    const [contactContainers, setContactsContainers] = useState([]);
    const currencyFromServer = useSelector(state => state.buildings.currentBuildingData?.currency ?? state.buildings.managerData?.currency);
    const currency = currencyFromServer ?? constants.defaultCurrency;
    const [contactInfo, setContactInfo] = useState({
        tenantType: dropdownTypeOptions[0],
        name: '',
        email: '',
        phone: ''
    });

    useEffect(async () => {
        if (!client) return
        let contacts = 0;
        let activeTenants = [];
        apt?.tenants.map(tenant => {
            if (tenant.tenant.isActive) {
                contacts += 1
                activeTenants.push(tenant)
            }
            return contacts
        });
        setContactsNum(contacts);
        setTenants(activeTenants);
    }, [apt, client]);

    useEffect(() => {
        if (edit) {
            setContactInfo({
                ...contactInfo,
                name: tenants[selected-1].details.name,
                email: tenants[selected-1].details.email,
                phone: tenants[selected-1].details.phone
            })
        } else {
            setContactInfo({
                ...contactInfo,
                name: '',
                email: '',
                phone: ''
            })
        }
    }, [edit, add]);

    const onContactInfoChange = (e, field) => {
        setContactInfo({
            ...contactInfo,
            [field]: e.target.value
        })
    };

    const onTypeDropdownChange = e => {
        setContactInfo({
            ...contactInfo,
            tenantType: e
        });
    };

    const onNextClick = () => {
        setEdit(false);
        setAdd(false);
        if (selected < contactsNum) {
            setSelected(selected+1);
            CacheData.cache('currentContactInTenantList', selected+1);
        }
        else {
            setSelected(1);
            CacheData.cache('currentContactInTenantList', 1);
        }
    };
    const onPrevClick = () => {
        setEdit(false);
        setAdd(false);
        if (selected > 1) {
            setSelected(selected-1);
            CacheData.cache('currentContactInTenantList', selected-1);
        }
        else {
            setSelected(contactsNum);
            CacheData.cache('currentContactInTenantList', contactsNum);
        }
    };

    useEffect(() => {
        const containers = [];
        tenants.sort((a, b) => {
            if (a.tenant.isPrimary === b.tenant.isPrimary) {
                if (a.tenantType === 'renter')  return -1
                else return 1
            } else if (a.tenant.isPrimary) return -1
            else return 1;
        });
        tenants.map(({ tenant, details }, i) => {
            const container = (
                <div className='contact-info' key={i}>
                    <div>
                        <div className='inner-title'>{t('reports.ownership_type')}</div>
                        <div className='value'>{t(`payments.${tenant.tenantType}`)}</div>
                    </div>

                    <div>
                        <div className='inner-title'>{t('expenses.name')}</div>
                        <div className='value'>{details.name}</div>
                    </div>

                    <div>
                        <div className='inner-title'>{t('expenses.phone_number')}</div>
                        <div className='value'>{details.phone}</div>
                    </div>

                    <div>
                        <div className='inner-title'>{t('expenses.email')}</div>
                        <div className='value'>{details.email}</div>
                    </div>
                </div>
            );
            containers.push(container);
            return containers
        });
        setContactsContainers(containers);
        setSelected(currentContact || 1);
    }, [tenants]);

    const handleContactAdd = () => {
        setShowPopover(false);
        setAdd(true);
    };

    const handleContactEdit = () => {
        setShowPopover(false);
        setEdit(true);
    };

    const handleContactDelete = async () => {
        setShowPopover(false);
        dispatch(showLoader());
        const tenant = tenants[selected-1].tenant;
        let typeOfTenant = t(`payments.${tenant.tenantType}`)
        const tenantID = tenant.id;
        const response = await client.tenants.deleteTenant(buildingID, apt.apartmentNum, tenantID);
        const didntManagerToFindNewPrimary = response.oldPrimaryTenant && !response.newPrimaryTenant

        if (response.newPrimaryTenant) {
            await loadApartments();
        } else if (didntManagerToFindNewPrimary){
            NotificationManager.warning(t('buildings.didntManagerToFindNewPrimary', {typeOfTenant}));
        } else {
            NotificationManager.error(t('buildings.something_wrong'));
        }
        dispatch(hideLoader());
    };

    const onApartmentDelete = async () => {
        let response;
        try {
            response = await client.managers.operations.inactivateApartment(buildingID, apt.apartmentNum);
        } catch (e) {}

        if (response) {
            NotificationManager.success(t('reports.apartment_deleted', {apartmentNum: response.apartment.apartmentNum}));
            CacheData.delete('currentAptInTenantList');
            await loadApartments();
        }
    };

    const handleStartChatClick = () => {
        setShowPopover(false);
        const parsedPhone = parsePhoneToInternational(constants.defaultCurrency, tenants[selected-1]?.details?.phone);
        if (parsedPhone !== null) window.open(`https://api.whatsapp.com/send?phone=${parsedPhone}&source=&data=`);
        else NotificationManager.error(t('buildings.something_wrong'))
    };

    const addContact = async () => {
        dispatch(showLoader());
        const payload = {
            isPrimary: false,
            tenantType: contactInfo.tenantType.value,
            isActive: true,
            details: {
                name: contactInfo.name,
                email: contactInfo.email,
                phone: contactInfo.phone
            }
        };
        const response = await client.tenants.addTenant(buildingID, apt.apartmentNum, payload);
        if (response) {
            CacheData.cache('currentContactInTenantList', contactsNum+1);
            let renewedTenants = [...tenants];
            renewedTenants.push(response);
            setTenants(renewedTenants);

            const editedApartment = {...apt, tenants: renewedTenants};
            const currentAptIndex = apartments.indexOf(apt);
            const renewedApartments = [...apartments];
            renewedApartments.splice(currentAptIndex, 1, editedApartment);
            setApartments(renewedApartments);
            setAdd(false);
        } else NotificationManager.error(t('buildings.something_wrong'));
        dispatch(hideLoader());
    };

    const editContact = async () => {
        dispatch(showLoader());
        const tenantID = tenants[selected-1].tenant.id;
        const payload = {
            name: contactInfo.name,
            email: contactInfo.email,
            phone: contactInfo.phone
        }
        const response = await client.tenants.editTenant(buildingID, apt.apartmentNum, tenantID, payload);
        if (response.details) {
            let editedTenant = tenants.find(({ tenant }) => tenant.id === tenantID);
            editedTenant = {tenant: editedTenant.tenant, details: response.details};

            let renewedTenants = [...tenants];
            renewedTenants.splice(selected-1, 1, editedTenant);
            setTenants(renewedTenants);

            const editedApartment = {...apt, tenants: renewedTenants};
            const currentAptIndex = apartments.indexOf(apt);
            let renewedApartments = [...apartments];
            renewedApartments.splice(currentAptIndex, 1, editedApartment);
            setApartments(renewedApartments);
            setEdit(false);
        } else NotificationManager.error(t('buildings.something_wrong'));
        dispatch(hideLoader());
    };

    const onIsPrimaryChange = async () => {
        dispatch(showLoader());
        const currentTenantId = tenants[selected-1].tenant.id;
        const shouldBePrimary = !tenants[selected-1].tenant.isPrimary;
        const response = await client.tenants.changeIsPrimary(buildingID, apt.apartmentNum, currentTenantId, shouldBePrimary);
        if (response.newPrimaryTenant || response.oldPrimaryTenant) await loadApartments();
        else NotificationManager.error(t('buildings.something_wrong'));
        dispatch(hideLoader());
    };

    const onBuildingManagerClick = async () => {
        const tenantID = tenants[selected-1]?.tenant?.id;
        const managerValue = !tenants[selected-1]?.tenant?.buildingManager;
        dispatch(showLoader());
        try {
            const response = await client.managers.buildings.markTenantAsBuildingManager(buildingID, apt.apartmentId, tenantID, managerValue);
            let editedTenant = tenants.find(({ tenant }) => tenant.id === tenantID);
            editedTenant = {tenant: {...editedTenant.tenant, buildingManager: managerValue}, details: editedTenant.details};

            let renewedTenants = [...tenants];
            renewedTenants.splice(selected-1, 1, editedTenant);
            setTenants(renewedTenants);

            const editedApartment = {...apt, tenants: renewedTenants};
            const currentAptIndex = apartments.indexOf(apt);
            let renewedApartments = [...apartments];
            renewedApartments.splice(currentAptIndex, 1, editedApartment);
            setApartments(renewedApartments);
            setEdit(false);
            await loadApartments();
        } catch (e) {
            NotificationManager.error(e.message);
        } finally {
            dispatch(hideLoader());
        }
    };

    const onLegalIssuesClick = async () => {
        dispatch(showLoader());
        const tenantID = tenants[selected-1]?.tenant?.id;
        const legalValue = !tenants[selected-1]?.tenant?.legalIssues;
        try {
            const response = await client.managers.buildings.markTenantAsInLegalIssues(buildingID, apt.apartmentId, tenantID, legalValue);
            let editedTenant = tenants.find(({ tenant }) => tenant.id === tenantID);
            editedTenant = {tenant: {...editedTenant.tenant, legalIssues: legalValue}, details: editedTenant.details};

            let renewedTenants = [...tenants];
            renewedTenants.splice(selected-1, 1, editedTenant);
            setTenants(renewedTenants);

            const editedApartment = {...apt, tenants: renewedTenants};
            const currentAptIndex = apartments.indexOf(apt);
            let renewedApartments = [...apartments];
            renewedApartments.splice(currentAptIndex, 1, editedApartment);
            setApartments(renewedApartments);
            setEdit(false);
            await loadApartments();
        } catch (e) {
            NotificationManager.error(e.message);
        } finally {
            dispatch(hideLoader());
        }
    };

    const onAddPaymentClick = () => {
        history.push({
            pathname: `/${constants.pages.tenantDetailsManagerUrl}/${buildingID}?apt=${apt?.apartmentNum}`
        });
    };

    return (
        <ApartmentInfoStyled edit={edit} add={add} ltr={ltr}>
            <div className='top-container'>
                <div>{t('payments.number_of_apartment')}: {apt?.apartmentNum}</div>
                <Button outlined rounded={false} onClick={onAddPaymentClick}>{t('reports.add_payment')}</Button>
            </div>

            <div className='total-contacts'>
                <div>{t('reports.total_contacts')}: {contactsNum}</div>
                <OverlayTrigger
                    rootClose
                    show={showPopover}
                    onToggle={() => setShowPopover(!showPopover)}
                    trigger="click"
                    placement="bottom"
                    overlay={(
                        <StyledPopover>
                            <Popover.Body>
                                <button className='link' onClick={handleContactAdd}>
                                    {t('reports.add_contact')}
                                </button>

                                <button className='link' onClick={handleContactEdit}>
                                    {t('reports.edit_contact')}
                                </button>

                                <button className='link' onClick={handleContactDelete}>
                                    {t('reports.delete_contact')}
                                </button>

                                {tenants[selected-1]?.details?.phone && (
                                    <button className='link' onClick={handleStartChatClick}>
                                        {t('payments.start_chat')}
                                    </button>
                                )}

                                {isAdmin ?
                                    <button className='link' onClick={() => {
                                        setShowPopover(false);
                                        onApartmentDelete();
                                    }}
                                    >
                                        {t('reports.delete_apartment')}
                                    </button>
                                    : null}
                            </Popover.Body>
                        </StyledPopover>
                    )
                }>
                    <div className='tool'>
                        <div className='dot'></div>
                        <div className='dot'></div>
                        <div className='dot'></div>
                    </div>
                </OverlayTrigger>
            </div>

            <div className='apt-details-header'>{t('reports.apartment.tenantDetails')}</div>

            {contactContainers.length > 0 && !edit && !add && contactContainers[selected-1]}
            {(edit || add) && (
                <AddOrEditContact
                    contactInfo={contactInfo}
                    onContactInfoChange={onContactInfoChange}
                    onTypeDropdownChange={onTypeDropdownChange}
                    selectedTenant={tenants[selected-1]}
                    edit={edit}
                    add={add}
                    dropdownTypeOptions={dropdownTypeOptions}
                    ltr={ltr}
                />
            )}

            <div className='action-icons'>
                {!add && (
                    <div className='icons-wrapper'>
                        <OverlayTrigger
                            placement="top"
                            overlay={props => (
                                <Tooltip {...props}>
                                    {t('reports.mark_manager')}
                                </Tooltip>
                            )}
                        >
                            <img src={tenants[selected-1]?.tenant?.buildingManager ? manager : disabledManager} onClick={onBuildingManagerClick} />
                        </OverlayTrigger>
                        <OverlayTrigger
                            placement="top"
                            overlay={props => (
                                <Tooltip {...props}>
                                    {t('reports.mark_legal')}
                                </Tooltip>
                            )}
                        >
                            <img src={tenants[selected-1]?.tenant?.legalIssues ? hammer : disabledHammer} onClick={onLegalIssuesClick} />
                        </OverlayTrigger>
                        <OverlayTrigger
                            placement="top"
                            overlay={props => (
                                <Tooltip {...props}>
                                    {t('reports.mark_primary')}
                                </Tooltip>
                            )}
                        >
                            <img src={tenants[selected-1]?.tenant.isPrimary ? star : disabledStar} onClick={onIsPrimaryChange} />
                        </OverlayTrigger>
                    </div>
                )}

                <div className='arrows'>
                    <div className='arrow prev' onClick={onPrevClick}><img src={arrowLeft} /></div>
                    <Dots dotsNum={!add ? contactsNum : contactsNum + 1} selected={!add ? selected : contactsNum + 1} />
                    <div className='arrow next' onClick={onNextClick}><img src={arrowLeft} /></div>
                </div>
            </div>

            {(edit || add) && (
                <div className='button-wrapper'>
                    <Button rounded={false} onClick={edit ? editContact : addContact}>{t('reports.save')}</Button>
                </div>
            )}

            <div className='monthly-fee'>
                <span>{t('reports.monthlyFee')}:</span>
                <CurrencyValue currency={currency}>{apt?.monthlyFee}</CurrencyValue>
            </div>
        </ApartmentInfoStyled>
    )
}

export default ApartmentInfo;
